import React, { useState, useEffect } from "react";
import moment from "moment";

import CustomDropDown from "input/CustomDropDown";
import { renderLoader } from "utility/LoaderUtils";

const UsageStatsPanel = ({
    coachingPlanDataFound,
    currentWeek,
    dataMissing,
    isLoading,
    rawUsageStatsData
}) => {
    const [isConfigured, setIsConfigured] = useState<boolean>(false);
    const [selectedWeek, setSelectedWeek] = useState<string>("total");
    const [weekOptions, setWeekOptions] = useState<{}[]>(null);
    const [weeklyStats, setWeeklyStats] = useState<{}>(null);

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            if (rawUsageStatsData) {
                let size = Object.keys(rawUsageStatsData).length - 1;
                let track = Object.keys(rawUsageStatsData)[size];
                let currentTrack = rawUsageStatsData[track];

                if (currentTrack.plan_weeks) {
                    let statsByWeek = {};
                    let totalStats = { sessions: 0, time: 0, bonusStart: 0, bonusCompletions: 0, routineStart: 0, routineCompletions: 0, mood: [], pain: [] };

                    let options = [];
                    Object.keys(currentTrack.plan_weeks).forEach(
                        (weekKey: string, i: number) => {
                            if (i <= currentWeek) {
                                options.push({
                                    label: `Week ${i.toString()}`,
                                    value: i.toString()
                                });
                            }

                            let weekData = currentTrack.plan_weeks[weekKey];
                            let weekStats = { sessions: 0, time: 0, bonusStart: 0, bonusCompletions: 0, routineStart: 0, routineCompletions: 0, mood: -1, pain: -1 };
                            if (weekData.visits) {
                                weekStats["sessions"] += weekData.unique_days;
                                totalStats["sessions"] += weekData.unique_days;
                            }
                            if (weekData.checkins) {
                                let pain = weekData.checkins[0].pain_level;
                                let mood = weekData.checkins[0].mood_level;
                                weekStats.mood = mood;
                                weekStats.pain = pain;
                                totalStats.mood.push([mood, i]);
                                totalStats.pain.push([pain, i]);
                            }
                            if (weekData.time_spent !== undefined) {
                                weekStats["time"] += weekData.time_spent;
                                totalStats["time"] += weekData.time_spent;
                            }
                            if (weekData.bonus_activity !== undefined) {
                                weekStats["bonusStart"] += weekData.bonus_activity.starts;
                                weekStats["bonusCompletions"] += weekData.bonus_activity.completions;
                                totalStats["bonusStart"] += weekData.bonus_activity.starts;
                                totalStats["bonusCompletions"] += weekData.bonus_activity.completions;
                            }
                            if (weekData.personal_routine !== undefined) {
                                weekStats["routineStart"] += weekData.personal_routine.starts;
                                weekStats["routineCompletions"] += weekData.personal_routine.completions;
                                totalStats["routineStart"] += weekData.personal_routine.starts;
                                totalStats["routineCompletions"] += weekData.personal_routine.completions;
                            }
                            statsByWeek[i] = weekStats;
                        }
                    );
                    statsByWeek["total"] = totalStats;

                    setWeeklyStats(statsByWeek);
                    // Sort the options by week and prepend the "All Time" option to the beginning.
                    const sortedOptions = [
                        { label: "All Time",  value: "total" },
                        ...options.sort((a, b) =>
                            a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" })
                        )
                    ];
                    setWeekOptions(sortedOptions);
                    setSelectedWeek(sortedOptions.at(-1).value);
                    setIsConfigured(true);
                }
            }
        }, [rawUsageStatsData]
    );

    // ---=== Render ===--- //

    const renderMoodPain = (view: string, data) => {
        if (view === "total") {
            if (data.length === 0) {
                return "No data found";
            }
            else if (data.length === 1) {
                return (
                    <p>
                        {data[0][0]}
                        {" "}
                        from week
                        {" "}
                        {data[0][1]}
                    </p>
                );
            }
            else {
                let change = (data[data.length - 1][0] - data[0][0]) / data[0][0];
                change *= 100;
                return (
                    <p className={change > 0 ? "text-success-600" : "text-warning-600"}>
                        {`${change > 0 ? "+" : ""}${Math.round(change)}%`}
                    </p>
                );
            }
        }
        else {
            if (data !== -1) {
                return data;
            }
            else {
                return "-";
            }
        }
    };

    const renderTable = () => {
        let table = [];

        table.push(
            <React.Fragment key={`usage-stats-panel-row-1`}>
                <tr className="border-0 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 grow shrink border-t-0 border-b-0 border-x-0 px-6 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <p className="whitespace-nowrap  inline-flex"><span className="text-gray-900">VET™ Sessions</span></p>
                        <div className="align-text-bottom has-tooltip float-bottom text-center content-center items-center pl-2 object-bottom whitespace-nowrap inline-flex">
                            <span className="tooltip rounded shadow-lg p-1 -mt-12 ml-2">
                                Unique days in which the patient completed at least one VET™ activity
                            </span>
                            <img className="h-4 w-4 hs-tooltip " src={"/icons/help-circle.svg"} alt="icon"/>
                        </div>
                        <div className="float-end">{weeklyStats[selectedWeek].sessions}</div>
                    </td>
                    <td className="w-1/2 border-t-0 border-x-0 px-6 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Reported Pain</span>
                        <div className="float-end">{renderMoodPain(selectedWeek, weeklyStats[selectedWeek].pain)}</div>
                    </td>
                </tr>
            </React.Fragment>
        );

        let time = weeklyStats[selectedWeek].time;
        table.push(
            <React.Fragment key={`usage-stats-panel-row-2`}>
                <tr className="border-y-2 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">VET™ Time</span>
                        <div className="float-end">
                            {
                                time > 60
                                    ? ((Math.floor(moment.duration(time * 1000).asHours()) > 0 ? Math.floor(moment.duration(time * 1000).asHours()) + "h " : "") + (moment.duration(time * 1000).minutes() > 0 ? moment.duration(time * 1000).minutes() + "m " : ""))
                                    : moment.duration(time * 1000).seconds() + "s "
                            }
                        </div>
                    </td>
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Reported Mood</span>
                        <div className="float-end">{renderMoodPain(selectedWeek, weeklyStats[selectedWeek].mood)}</div>
                    </td>
                </tr>
            </React.Fragment>
        );

        table.push(
            <React.Fragment key={`usage-stats-panel-row-3`}>
                <tr className="border-y-2 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Bonus Activity Starts</span>
                        <div className="float-end">{weeklyStats[selectedWeek].bonusStart}</div>
                    </td>
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Routine Starts</span>
                        <div className="float-end">{weeklyStats[selectedWeek].routineStart}</div>
                    </td>
                </tr>
            </React.Fragment>
        );

        table.push(
            <React.Fragment key={`usage-stats-panel-row-4`}>
                <tr className="border-y-2 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Bonus Activity Completions</span>
                        <div className="float-end">{weeklyStats[selectedWeek].bonusCompletions}</div>
                    </td>
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Routine Completions</span>
                        <div className="float-end">{weeklyStats[selectedWeek].routineCompletions}</div>
                    </td>
                </tr>
            </React.Fragment>
        );

        return table;
    };

    return (
        <React.Fragment>
            {
                (isLoading || !isConfigured) && coachingPlanDataFound && !dataMissing
                    ? (
                        renderLoader("Loading Usage Data", 50)
                    )
                    : (
                        <div className="w-full h-[90%] justify-start items-center inline-flex flex-col">
                            {
                                dataMissing
                                    ? (
                                        <div className="w-full h-full justify-center gap-6 items-center inline-flex flex-col">
                                            <div className="w-14 h-14 p-3.5 bg-[#ffe9e2] rounded-full border border-gray-50 justify-center items-center inline-flex">
                                                <img className="w-7 h-7 relative flex-col justify-start items-start flex" src="/icons/data.svg" alt="icon"/>
                                            </div>
                                            <div className="text-wrap w-[80%] break-words">
                                                <div className="self-stretch text-center text-gray-700 text-md font-semibold leading-[18px]">No Usage Stats Available</div>
                                            </div>
                                            <div className="break-words inline-block text-center text-gray-500 text-base font-normal">
                                                {
                                                    coachingPlanDataFound
                                                        ? (
                                                            <>
                                                                Coaching plan active. No stats recorded yet.
                                                            </>
                                                        )
                                                        : (
                                                            <>
                                                                No active coaching plan found.
                                                            </>
                                                        )
                                                }
                                            </div>
                                        </div>
                                    )
                                    : (
                                        <React.Fragment>
                                            <div className="w-full h-[10%] gap-5 inline-flex justify-start items-center">
                                                <div className="grow shrink basis-0 text-gray-900 text-xl font-semibold ml-[26px]">
                                                    Usage Stats
                                                </div>
                                                <div className="flex-col justify-start items-start inline-flex mr-[26px]">
                                                    <CustomDropDown
                                                        defaultValue={selectedWeek}
                                                        options={weekOptions}
                                                        // Callbacks
                                                        onSelectionCallback={setSelectedWeek}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-[calc(100%-48px)] h-[80%] border-0 mt-5">
                                                <table className="w-full h-[100%] divide-gray-700 border-0 dark:divide-gray-700 rounded-[10px]">
                                                    <tbody className="border-0">
                                                        {renderTable()}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </React.Fragment>
                                    )
                            }
                        </div>
                    )
            }
        </React.Fragment>
    );
};

export default UsageStatsPanel;
